import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Beaker, Calculator, BookMarked, GraduationCap } from 'lucide-react';

const Classes = () => {
  const classes = [
    { level: '6', name: 'Class 6th', icon: GraduationCap },
    { level: '7', name: 'Class 7th', icon: GraduationCap },
    { level: '8', name: 'Class 8th', icon: GraduationCap },
    { level: '9', name: 'Class 9th', icon: GraduationCap },
    { level: '10', name: 'Class 10th', icon: GraduationCap },
  ];

  const seniorClasses = [
    { level: '11', name: 'Class 11th', icon: GraduationCap },
    { level: '12', name: 'Class 12th', icon: GraduationCap },
  ];

  const streams = [
    { id: 'commerce', name: 'Commerce', icon: Calculator, description: 'Business, Accounts, Economics' },
    { id: 'science', name: 'Science', icon: Beaker, description: 'Physics, Chemistry, Mathematics' },
    { id: 'biology', name: 'Biology', icon: BookMarked, description: 'Physics, Chemistry, Biology' },
    { id: 'humanities', name: 'Humanities', icon: BookOpen, description: 'History, Political Science, Geography' },
  ];

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="classes-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <span className="text-sm uppercase tracking-[0.2em] font-semibold text-zinc-500">Education</span>
          <h1 className="font-outfit text-4xl md:text-5xl font-black text-white mt-2">
            Browse <span className="text-red-500">Classes</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl">
            Explore our comprehensive curriculum from class 6th to 12th. Each class contains videos, 
            PDFs, PPTs, and study materials curated by expert educators.
          </p>
        </div>

        {/* Middle School Classes (6-10) */}
        <div className="mb-16">
          <h2 className="font-outfit text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-red-500" />
            Middle School
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {classes.map((cls, idx) => (
              <Link
                key={cls.level}
                to={`/classes/${cls.level}`}
                className={`card-hover bg-[#121212] p-8 text-center rounded-sm animate-fadeIn stagger-${idx + 1}`}
                data-testid={`class-${cls.level}-link`}
              >
                <GraduationCap className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <div className="font-outfit text-4xl font-black text-white mb-1">{cls.level}</div>
                <div className="text-sm text-zinc-500">{cls.name}</div>
              </Link>
            ))}
          </div>
        </div>

        {/* Senior Secondary (11-12) */}
        <div className="mb-16">
          <h2 className="font-outfit text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-red-500" />
            Senior Secondary
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {seniorClasses.map((cls, idx) => (
              <div
                key={cls.level}
                className={`card-hover bg-[#121212] p-8 rounded-sm animate-fadeIn stagger-${idx + 1}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="font-outfit text-5xl font-black text-white">{cls.level}</div>
                  <div>
                    <div className="text-lg font-semibold text-white">{cls.name}</div>
                    <div className="text-sm text-zinc-500">Choose your stream</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {streams.map((stream) => (
                    <Link
                      key={stream.id}
                      to={`/classes/${cls.level}/${stream.id}`}
                      className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-zinc-800 rounded-sm hover:border-red-500/50 transition-colors"
                      data-testid={`class-${cls.level}-${stream.id}`}
                    >
                      <stream.icon className="w-5 h-5 text-red-500" />
                      <span className="text-sm text-zinc-300">{stream.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Streams Overview */}
        <div>
          <h2 className="font-outfit text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-red-500" />
            Available Streams
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {streams.map((stream, idx) => (
              <div
                key={stream.id}
                className={`card-hover bg-[#121212] p-6 rounded-sm animate-fadeIn stagger-${idx + 1}`}
                data-testid={`stream-overview-${stream.id}`}
              >
                <div className="w-12 h-12 rounded-sm bg-red-500/10 flex items-center justify-center mb-4">
                  <stream.icon className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="font-semibold text-white mb-2">{stream.name}</h3>
                <p className="text-sm text-zinc-500 mb-4">{stream.description}</p>
                <div className="text-xs text-zinc-600">Available for Class 11th & 12th</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Classes;
